import React, { useContext, useReducer, useState } from 'react'
import { Button, Form, Input, NotificationManagerContext, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { CloseIcon } from 'assets/icons'
import { useUpdateInitiative } from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'

const reducer = (state, action) => {
   switch (action.type) {
      case 'addOption': {
         return [...state, { value: '', key: `y${Date.now()}` }]
      }

      case 'updateOption': {
         return state.map((option, i) => {
            return i === action.index
               ? { ...option, value: action.value }
               : option
         })
      }

      case 'removeOption': {
         return state.filter((option, i) => i !== action.index)
      }

      case 'reorder': {
         const newState = state.slice()
         const [removed] = newState.splice(action.srcIndex, 1)
         newState.splice(action.destIndex, 0, removed)
         return newState
      }

      default: {
         throw new Error()
      }
   }
}

const initPollOptions = (options = []) => {
   return options.map((optionValue, i) => ({
      key: `x${i}`,
      value: optionValue
   }))
}

const PollEditor = ({ onCloseButtonClick, onSave }) => {
   const { showNotification } = useContext(NotificationManagerContext)
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()

   // Only deal with one question for now, which we will call "poll"
   const poll = initiative.appsSurveyFormElements?.[0]
   const [pollLabel, setPollLabel] = useState(poll?.label ?? '')
   const [pollOptions, dispatch] = useReducer(reducer, [], () => initPollOptions(poll?.options))

   const handleSave = async () => {
      try {
         const surveyFormElementsOps = initiative.appsSurveyEnabled && poll?.id
            ? {
               operation: 'update',
               update: {
                  id: poll.id,
                  label: pollLabel,
                  options: pollOptions.map(o => o.value)
               }
            }
            : {
               operation: 'create',
               create: {
                  type: 'radio',
                  label: pollLabel,
                  options: pollOptions.map(o => o.value)
               }
            }

         await updateInitiative({ id: initiative.id }, {
            appsSurveyEnabled: true,
            appsSurveyFormElements: [surveyFormElementsOps]
         })

         onSave()
      } catch (error) {
         showNotification({
            message: error.graphQLErrors.map(e => e.message).join(', ') || error.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   const handleDragEnd = (result) => {
      // dropped outside the list
      if (!result.destination) {
         return
      }

      const srcIndex = result.source.index
      const destIndex = result.destination.index
      dispatch({ type: 'reorder', srcIndex, destIndex })
   }

   const handleAddOption = () => {
      dispatch({ type: 'addOption' })
   }

   const handleRemoveOption = (index) => {
      dispatch({ type: 'removeOption', index })
   }

   const toggleSurveyEnabled = async () => {
      await updateInitiative({ id: initiative.id }, {
         appsSurveyEnabled: !initiative.appsSurveyEnabled
      })
   }

   const handleFieldChange = (i, newValue) => {
      dispatch({ type: 'updateOption', index: i, value: newValue })
   }

   return (
      <View width="100%" height="100%">
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            height="60px"
            padding="0px 20px"
            borderBottom="1px solid #eee">
            <Text size="2.5" weight="medium">
               {`${initiative.type.nameSingular} Poll`}
            </Text>
            <View onClick={onCloseButtonClick}>
               <CloseIcon
                  width="20px"
                  height="20px"
                  strokeWidth="2px"
                  color="#aaa"
               />
            </View>
         </View>
         <View
            width="100%"
            height="calc(100% - 120px)"
            padding="30px 20px"
            scroll="on">
            <Text size="2" color="#333">
               {`Create a poll for the ${initiative.type.nameSingular}: `}
               <b>{initiative.name}</b>
               {' The poll cannot be changed once voting has begun.'}
            </Text>
            <Form
               elementAppearance="plain"
               elementBackgroundColor="#eee"
               elementFocusBackgroundColor="#f3f3f3"
               labelTextColor="#333"
               labelTextSize="1.5"
               labelTextTransform="none"
               labelTextWeight="medium">
               <View marginTop="30px" marginBottom="10px">
                  <Text size="1.5" weight="medium">
                     Poll Question
                  </Text>
               </View>
               <Input
                  value={pollLabel}
                  onChange={e => setPollLabel(e.target.value)}
               />
               <View marginTop="20px" marginBottom="10px">
                  <Text size="1.5" weight="medium">
                     Poll Answers
                  </Text>
               </View>
               <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="droppable">
                     {(droppableProvided, droppableSnapshot) => (
                        <div
                           {...droppableProvided.droppableProps}
                           ref={droppableProvided.innerRef}>
                           {pollOptions.map((option, index) => (
                              <Draggable key={option.key} draggableId={`${option.key}`} index={index}>
                                 {(itemProvided, itemSnapshot) => (
                                    <div
                                       ref={itemProvided.innerRef}
                                       {...itemProvided.draggableProps}
                                       {...itemProvided.dragHandleProps}
                                       style={{
                                          ...itemProvided.draggableProps.style,
                                          left: '0px !important'
                                       }}>
                                       <View
                                          display="flex"
                                          alignItems="center"
                                          marginTop="5px">
                                          <View flex="1 1 auto">
                                             <Input
                                                maxLength="40"
                                                value={option.value}
                                                onChange={(e) => {
                                                   handleFieldChange(index, e.target.value)
                                                }}
                                                placeholder="Poll Option"
                                             />
                                          </View>
                                          <View
                                             display="flex"
                                             justifyContent="center"
                                             alignItems="center"
                                             onClick={() => handleRemoveOption(index)}
                                             flex="0 0 auto"
                                             marginLeft="10px"
                                             width="20px"
                                             height="20px"
                                             borderRadius="50%"
                                             backgroundColor="#eee">
                                             <CloseIcon
                                                width="12px"
                                                height="12px"
                                                color="#333"
                                             />
                                          </View>
                                       </View>
                                    </div>
                                 )}
                              </Draggable>
                           ))}
                           {droppableProvided.placeholder}
                        </div>
                     )}
                  </Droppable>
               </DragDropContext>
            </Form>
            <View marginTop="5px">
               <Button
                  onClick={handleAddOption}
                  color="#000"
                  textColor="#fff"
                  name="Add Poll Option"
                  size="xs"
               />
            </View>
         </View>
         <View
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            padding="0px 15px"
            width="100%"
            borderTop="1px solid #eee"
            height="60px">
            {initiative.appsSurveyEnabled && (
               <Button
                  onClick={toggleSurveyEnabled}
                  name="Disable Poll"
                  color="#eee"
                  textColor="#333"
               />
            )}
            <Button onClick={handleSave} name="Save" color="#000" textColor="#fff" />
         </View>
      </View>
   )
}

PollEditor.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired,
   onSave: PropTypes.func.isRequired
}

export default PollEditor
